import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, X } from "lucide-react";

import {
  createPolicyRule,
  getPolicy,
  getProjectSelection,
  getServer,
  listAccessTokens,
  listChannels,
  listServers,
  updatePolicyRule,
  type AccessToken,
  type Channel,
  type PolicyAction,
  type ServerTool,
} from "@/lib/api";
import { humanizeError } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import { Spinner } from "@/components/Page";
import { CreatePage, Section, WizardFooter, useStepper, type WizardStep } from "@/components/wizard";
import { cn } from "@/lib/utils";
import {
  OPS,
  SELECT_CLS,
  TTL_PRESETS,
  draftToInput,
  parseToolSel,
  resolveEnabled,
  ruleToDraft,
  schemaFields,
  type Draft,
  type DraftArg,
  type EnabledServer,
} from "@/lib/policyRule";

const STEPS: WizardStep[] = [
  { id: "match", title: "Match", description: "Who + which tool + conditions" },
  { id: "action", title: "Action", description: "Allow, hold, or deny" },
  { id: "review", title: "Review", description: "Confirm + create" },
];

const PATH_LIST_ID = "rule-arg-paths";

export function RuleEditor() {
  const { projectId = "", ruleId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const backTo = `/agents/${projectId}/settings/policy`;
  const editing = !!ruleId;

  const [d, setD] = useState<Draft | null>(null);
  const [tokens, setTokens] = useState<AccessToken[]>([]);
  const [enabled, setEnabled] = useState<EnabledServer[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [toolDetail, setToolDetail] = useState<Record<string, ServerTool[]>>({});
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const step = useStepper(STEPS.length);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [pol, toks, servers, sel, chans] = await Promise.all([
          getPolicy(projectId),
          listAccessTokens(projectId).catch(() => [] as AccessToken[]),
          listServers().catch(() => []),
          getProjectSelection(projectId).catch(() => null),
          listChannels(projectId).then((r) => r.channels).catch(() => [] as Channel[]),
        ]);
        if (cancelled) return;
        setTokens(toks);
        setEnabled(resolveEnabled(servers, sel));
        setChannels(chans);
        const rule = editing ? (pol.rules ?? []).find((r) => r.id === ruleId) ?? null : null;
        if (editing && !rule) {
          setLoadErr("That rule no longer exists.");
          return;
        }
        setD(ruleToDraft(rule));
      } catch (e) {
        if (!cancelled) setLoadErr(humanizeError(e, "Couldn't load the rule editor"));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, ruleId, editing]);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((cur) => (cur ? { ...cur, [k]: v } : cur));

  async function onSubmit() {
    if (!d) return;
    setSaving(true);
    try {
      const input = draftToInput(d);
      if (editing && ruleId) await updatePolicyRule(projectId, ruleId, input);
      else await createPolicyRule(projectId, input);
      toast.success(editing ? "Rule updated." : "Rule created.");
      navigate(backTo);
    } catch (e) {
      toast.error(humanizeError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  }

  if (loadErr) {
    return (
      <CreatePage title="Policy rule" backTo={backTo} footer={<Button className="ml-auto" onClick={() => navigate(backTo)}>Back</Button>}>
        <Section title="Couldn't open the editor">
          <p className="text-sm text-destructive">{loadErr}</p>
        </Section>
      </CreatePage>
    );
  }
  if (!d) {
    return (
      <CreatePage title="Policy rule" backTo={backTo} footer={<span />}>
        <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
          <Spinner /> Loading…
        </div>
      </CreatePage>
    );
  }

  return (
    <CreatePage
      title={editing ? "Edit policy rule" : "New policy rule"}
      description="When a call matches the conditions, apply the action. Most-restrictive matching rule wins."
      backTo={backTo}
      steps={STEPS}
      current={step.current}
      furthest={step.furthest}
      onJump={step.goTo}
      footer={
        <WizardFooter
          onCancel={() => navigate(backTo)}
          onBack={step.back}
          onPrimary={() => (step.isLast ? void onSubmit() : step.next())}
          isFirst={step.isFirst}
          isLast={step.isLast}
          submitting={saving}
          submitLabel={editing ? "Save rule" : "Create rule"}
        />
      }
    >
      {step.current === 0 && (
        <MatchStep
          d={d}
          set={set}
          tokens={tokens}
          enabled={enabled}
          toolDetail={toolDetail}
          setToolDetail={setToolDetail}
        />
      )}
      {step.current === 1 && <ActionStep d={d} set={set} channels={channels} />}
      {step.current === 2 && <ReviewStep d={d} tokens={tokens} channels={channels} />}
    </CreatePage>
  );
}

function MatchStep({
  d,
  set,
  tokens,
  enabled,
  toolDetail,
  setToolDetail,
}: {
  d: Draft;
  set: <K extends keyof Draft>(k: K, v: Draft[K]) => void;
  tokens: AccessToken[];
  enabled: EnabledServer[];
  toolDetail: Record<string, ServerTool[]>;
  setToolDetail: React.Dispatch<React.SetStateAction<Record<string, ServerTool[]>>>;
}) {
  const toolSel = parseToolSel(d.tool);
  const setServerSel = (server: string) => set("tool", server ? `${server}.*` : "");
  const setToolSel = (name: string) =>
    set("tool", !toolSel.server ? "" : name === "*" ? `${toolSel.server}.*` : `${toolSel.server}.${name}`);

  useEffect(() => {
    const s = toolSel.server;
    if (!s || toolDetail[s]) return;
    let cancelled = false;
    void getServer(s)
      .then((det) => !cancelled && setToolDetail((m) => ({ ...m, [s]: det.tools ?? [] })))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [toolSel.server, toolDetail, setToolDetail]);

  const serverOptions = enabled.map((s) => s.name);
  if (toolSel.server && !serverOptions.includes(toolSel.server)) serverOptions.unshift(toolSel.server);
  const curServerTools = enabled.find((s) => s.name === toolSel.server)?.tools ?? [];
  const toolOptions = [...curServerTools];
  if (toolSel.tool && toolSel.tool !== "*" && !toolOptions.includes(toolSel.tool)) toolOptions.unshift(toolSel.tool);

  const selectedTool =
    toolSel.tool && toolSel.tool !== "*"
      ? (toolDetail[toolSel.server] ?? []).find((t) => t.name === toolSel.tool)
      : undefined;
  const argFields = selectedTool ? schemaFields(selectedTool.input_schema) : [];

  const addArg = () => set("args", [...d.args, { path: "", op: "eq", value: "" }]);
  const setArg = (i: number, patch: Partial<DraftArg>) =>
    set("args", d.args.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  const removeArg = (i: number) => set("args", d.args.filter((_, j) => j !== i));

  return (
    <>
      <Section title="Caller" description="Which identity this rule applies to.">
        <select className={SELECT_CLS} value={d.principal} onChange={(e) => set("principal", e.target.value)}>
          <option value="">Any caller</option>
          {tokens.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </Section>

      <Section title="Tool" description="The integration + tool this rule matches.">
        <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-2">
          <select className={SELECT_CLS} value={toolSel.server} onChange={(e) => setServerSel(e.target.value)}>
            <option value="">Any integration</option>
            {serverOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select
            className={SELECT_CLS}
            value={toolSel.server ? toolSel.tool : ""}
            disabled={!toolSel.server}
            onChange={(e) => setToolSel(e.target.value)}
          >
            {toolSel.server ? (
              <>
                <option value="*">All tools</option>
                {toolOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </>
            ) : (
              <option value="">— any tool —</option>
            )}
          </select>
        </div>
        {selectedTool && (
          <p className="text-xs text-muted-foreground">
            {selectedTool.description ? `${selectedTool.description} · ` : ""}
            {argFields.length > 0 ? (
              <>
                Arguments:{" "}
                {argFields.map((f, i) => (
                  <span key={f.path}>
                    {i > 0 && ", "}
                    <span className="font-mono text-foreground">{f.path}</span> ({f.type})
                  </span>
                ))}
              </>
            ) : (
              "No documented arguments."
            )}
          </p>
        )}
      </Section>

      <Section title="Argument conditions" description="All must match. Leave empty to match on caller + tool alone.">
        <datalist id={PATH_LIST_ID}>
          {argFields.map((f) => (
            <option key={f.path} value={f.path} />
          ))}
        </datalist>
        {d.args.length === 0 ? (
          <p className="text-xs text-muted-foreground">No conditions yet.</p>
        ) : (
          <div className="space-y-2">
            {d.args.map((a, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  className="flex-1"
                  list={PATH_LIST_ID}
                  value={a.path}
                  placeholder="path e.g. amount"
                  onChange={(e) => setArg(i, { path: e.target.value })}
                />
                <select className={cn(SELECT_CLS, "w-24")} value={a.op} onChange={(e) => setArg(i, { op: e.target.value })}>
                  {OPS.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </select>
                <Input
                  className="flex-1"
                  value={a.value}
                  disabled={a.op === "exists"}
                  placeholder={a.op === "in" ? `["a","b"]` : "value"}
                  onChange={(e) => setArg(i, { value: e.target.value })}
                />
                <Button type="button" variant="ghost" size="icon" aria-label="Remove condition" onClick={() => removeArg(i)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
        <Button type="button" variant="outline" size="sm" onClick={addArg}>
          <Plus className="h-3.5 w-3.5" /> Add condition
        </Button>
      </Section>
    </>
  );
}

function ActionStep({
  d,
  set,
  channels,
}: {
  d: Draft;
  set: <K extends keyof Draft>(k: K, v: Draft[K]) => void;
  channels: Channel[];
}) {
  const toggleChannel = (id: string) =>
    set("channels", d.channels.includes(id) ? d.channels.filter((c) => c !== id) : [...d.channels, id]);
  return (
    <>
      <Section title="Action" description="What happens when a call matches.">
        <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3">
          <label className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Action</span>
            <select className={SELECT_CLS} value={d.action} onChange={(e) => set("action", e.target.value as PolicyAction)}>
              <option value="ALLOW">ALLOW</option>
              <option value="HOLD">HOLD (require approval)</option>
              <option value="DENY">DENY</option>
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Priority</span>
            <Input type="number" value={d.priority} onChange={(e) => set("priority", parseInt(e.target.value, 10))} />
          </label>
        </div>
        <label className="block space-y-1">
          <span className="text-xs font-medium text-muted-foreground">Reason (shown to the agent on a block)</span>
          <Input value={d.reason} placeholder="e.g. Refunds over $100 need a human" onChange={(e) => set("reason", e.target.value)} />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={d.enabled} onChange={(e) => set("enabled", e.target.checked)} className="h-4 w-4 rounded border-input" />
          Enabled
        </label>
      </Section>

      {d.action === "HOLD" && (
        <Section title="Approval" description="How long the hold waits, and where the request is sent.">
          <label className="block space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Hold expires after</span>
            <select className={SELECT_CLS} value={d.ttlSecs} onChange={(e) => set("ttlSecs", parseInt(e.target.value, 10))}>
              {TTL_PRESETS.map((p) => (
                <option key={p.secs} value={p.secs}>
                  {p.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              How long the request stays pending for a human before it auto-expires. The agent isn't blocked meanwhile.
            </p>
          </label>
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Notify channels</span>
            {channels.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No channels configured. Add one in the Channels tab; until then approvals use the workspace default.
              </p>
            ) : (
              <div className="space-y-1 rounded-md border p-2">
                {channels.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={d.channels.includes(c.id)}
                      onChange={() => toggleChannel(c.id)}
                      className="h-4 w-4 rounded border-input"
                    />
                    <span>{c.name}</span>
                    {!c.enabled && <span className="text-xs text-muted-foreground">(disabled)</span>}
                  </label>
                ))}
                <p className="pt-1 text-xs text-muted-foreground">
                  {d.channels.length === 0
                    ? "None selected → notify all enabled channels."
                    : "Only the selected channel(s) get this rule's approval requests."}
                </p>
              </div>
            )}
          </div>
        </Section>
      )}
    </>
  );
}

function ReviewStep({ d, tokens, channels }: { d: Draft; tokens: AccessToken[]; channels: Channel[] }) {
  const caller = d.principal ? tokens.find((t) => t.id === d.principal)?.name ?? d.principal : "Any caller";
  const chanNames = useMemo(
    () => d.channels.map((id) => channels.find((c) => c.id === id)?.name ?? id),
    [d.channels, channels],
  );
  const ttlLabel = TTL_PRESETS.find((p) => p.secs === d.ttlSecs)?.label ?? `${d.ttlSecs}s`;
  return (
    <Section title="Review" description="Confirm the rule before saving.">
      <dl className="space-y-2 text-sm">
        <Row k="Action" v={d.action} />
        <Row k="Caller" v={caller} />
        <Row k="Tool" v={d.tool || "any tool"} />
        <Row k="Conditions" v={d.args.filter((a) => a.path.trim()).length === 0 ? "none" : `${d.args.filter((a) => a.path.trim()).length}`} />
        <Row k="Priority" v={String(d.priority)} />
        <Row k="Reason" v={d.reason || "—"} />
        {d.action === "HOLD" && <Row k="Hold expires" v={ttlLabel} />}
        {d.action === "HOLD" && <Row k="Notify" v={chanNames.length ? chanNames.join(", ") : "all enabled channels"} />}
        <Row k="Enabled" v={d.enabled ? "yes" : "no"} />
      </dl>
    </Section>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="shrink-0 text-muted-foreground">{k}</dt>
      <dd className="min-w-0 truncate text-right font-mono">{v}</dd>
    </div>
  );
}
